import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
  DeleteOutline as TrashIcon,
  EditOutlined as EditIcon,
  Close as CloseIcon,
  UndoOutlined as UndoIcon,
} from "@mui/icons-material";
import { dt } from "../themes/redesignTokens";

// ─── Types ──────────────────────────────────────────────────

export type ToastVariant = "resolve" | "delete" | "edit" | "error" | "success" | "undo";

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  duration: number;
  action?: React.ReactNode;
}

interface ActionToastProps {
  open: boolean;
  variant: ToastVariant;
  message: string;
  onClose: () => void;
  /** Auto-dismiss in ms (default 5000) */
  duration?: number;
  /** Optional action slot (e.g. Undo button) */
  action?: React.ReactNode;
}

// ─── Variant config ────────────────────────────────────────

const VARIANT_CONFIG: Record<
  ToastVariant,
  { accentColor: string; bgColor: string; icon: React.ReactNode }
> = {
  resolve: {
    accentColor: "#2D8C4E",
    bgColor: "#FFFFFF",
    icon: <ResolveIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />,
  },
  delete: {
    accentColor: "#3D3D3D",
    bgColor: "#3D3D3D",
    icon: <TrashIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />,
  },
  edit: {
    accentColor: "#D4572B",
    bgColor: "#FFFFFF",
    icon: <EditIcon sx={{ fontSize: 16, color: "#D4572B" }} />,
  },
  error: {
    accentColor: "#DC2626",
    bgColor: "#FFFFFF",
    icon: <CloseIcon sx={{ fontSize: 16, color: "#DC2626" }} />,
  },
  success: {
    accentColor: "#2D8C4E",
    bgColor: "#FFFFFF",
    icon: <ResolveIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />,
  },
  undo: {
    accentColor: "#3B82F6",
    bgColor: "#FFFFFF",
    icon: <UndoIcon sx={{ fontSize: 16, color: "#3B82F6" }} />,
  },
};

const MAX_VISIBLE_TOASTS = 5;
const TOAST_GAP = 10;
const TOAST_HEIGHT = 52; // approximate height per toast

// ─── Inject keyframes once ──────────────────────────────────

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  const id = "sr-toast-keyframes";
  if (document.getElementById(id)) { keyframesInjected = true; return; }
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes srToastSlideIn {
      0% { transform: translateX(120%); opacity: 0; }
      100% { transform: translateX(0); opacity: 1; }
    }
    @keyframes srToastSlideOut {
      0% { transform: translateX(0); opacity: 1; }
      100% { transform: translateX(120%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
  keyframesInjected = true;
}

// ─── Single toast item renderer ─────────────────────────────

interface ToastItemRendererProps {
  item: ToastItem;
  index: number;
  onDismiss: (id: number) => void;
}

const ToastItemRenderer: React.FC<ToastItemRendererProps> = ({ item, index, onDismiss }) => {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, item.duration);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [item.duration]);

  useEffect(() => {
    if (!exiting) return;
    const exitTimer = setTimeout(() => onDismiss(item.id), 250);
    return () => clearTimeout(exitTimer);
  }, [exiting, item.id, onDismiss]);

  const handleClose = useCallback(() => {
    setExiting(true);
  }, []);

  const config = VARIANT_CONFIG[item.variant];
  const isDark = item.variant === "delete";
  const topOffset = 16 + index * (TOAST_HEIGHT + TOAST_GAP);

  return (
    <Box
      data-testid="action-toast"
      sx={{
        position: "fixed",
        top: `${topOffset}px`,
        right: "16px",
        zIndex: 1500 + index,
        animation: exiting
          ? "srToastSlideOut 0.25s ease forwards"
          : "srToastSlideIn 0.3s ease",
        transition: "top 0.3s ease",
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: config.bgColor,
          borderRadius: "10px",
          borderLeft: `3px solid ${config.accentColor}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          minWidth: "280px",
          maxWidth: "400px",
        }}
      >
        {/* Icon */}
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {config.icon}
        </Box>

        {/* Message */}
        <Box
          sx={{
            flex: 1,
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "#FFFFFF" : "#0C1117",
          }}
        >
          {item.message}
        </Box>

        {/* Action slot (e.g. Undo button) */}
        {item.action && (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {item.action}
          </Box>
        )}

        {/* Close button */}
        <Box
          onClick={handleClose}
          sx={{
            display: "flex",
            cursor: "pointer",
            flexShrink: 0,
            opacity: 0.5,
            transition: "opacity 0.15s ease",
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16, color: isDark ? "#FFFFFF" : "#8C9099" }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Toast Stack component ──────────────────────────────────

interface ToastStackProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

export const ToastStack: React.FC<ToastStackProps> = ({ toasts, onDismiss }) => {
  useEffect(() => { injectKeyframes(); }, []);

  if (toasts.length === 0) return null;

  return (
    <>
      {toasts.map((toast, index) => (
        <ToastItemRenderer
          key={toast.id}
          item={toast}
          index={index}
          onDismiss={onDismiss}
        />
      ))}
    </>
  );
};

// ─── useToastStack hook ─────────────────────────────────────

let toastIdCounter = 0;

export function useToastStack() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (
      variant: ToastVariant,
      message: string,
      options?: { duration?: number; action?: React.ReactNode }
    ): number => {
      const id = ++toastIdCounter;
      const newToast: ToastItem = {
        id,
        variant,
        message,
        duration: options?.duration ?? 5000,
        action: options?.action,
      };
      setToasts((prev) => {
        const next = [...prev, newToast];
        // Cap visible toasts — remove oldest if over limit
        if (next.length > MAX_VISIBLE_TOASTS) {
          return next.slice(next.length - MAX_VISIBLE_TOASTS);
        }
        return next;
      });
      return id;
    },
    []
  );

  return { addToast, dismissToast, toasts };
}

// ─── Legacy single-toast component (backward compat) ────────

const ActionToast: React.FC<ActionToastProps> = ({
  open,
  variant,
  message,
  onClose,
  duration = 5000,
  action,
}) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [open, visible]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [open, duration, onClose]);

  if (!visible) return null;

  const config = VARIANT_CONFIG[variant];
  const isDark = variant === "delete";

  return (
    <Box
      data-testid="action-toast"
      sx={{
        position: "fixed",
        top: "16px",
        right: "16px",
        zIndex: 1500,
        animation: exiting
          ? "srToastSlideOut 0.25s ease forwards"
          : "srToastSlideIn 0.3s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: config.bgColor,
          borderRadius: "10px",
          borderLeft: `3px solid ${config.accentColor}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          padding: "12px 16px",
          minWidth: "280px",
          maxWidth: "400px",
        }}
      >
        {/* Icon */}
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {config.icon}
        </Box>

        {/* Message */}
        <Box
          sx={{
            flex: 1,
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "#FFFFFF" : "#0C1117",
          }}
        >
          {message}
        </Box>

        {/* Action slot (e.g. Undo button) */}
        {action && (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {action}
          </Box>
        )}

        {/* Close button */}
        <Box
          onClick={onClose}
          sx={{
            display: "flex",
            cursor: "pointer",
            flexShrink: 0,
            opacity: 0.5,
            transition: "opacity 0.15s ease",
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16, color: isDark ? "#FFFFFF" : "#8C9099" }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ActionToast;
